import type { ComponentChildren, JSX } from "preact";
import { createContext } from "preact";
import { useContext, useMemo, useState } from "preact/hooks";
import type { DimensionValue } from "@gss/types/Dimension";
import type { Month } from "@gss/types/Month";
import { en } from "../../translations/en";
import { sv } from "../../translations/sv";
import type {
    LanguageCode,
    LanguageTranslation,
} from "../../translations/translation.types";

interface Props {
    children: ComponentChildren;
}

export type GetTranslation = (
    texts: Record<string, string>,
) => string | undefined;

interface ContextResponse {
    translation: LanguageTranslation;
    // Used by menu
    languages: readonly LanguageTranslation[];
    changeLanguage: (lng: LanguageCode) => void;
    translateDimension: (value: DimensionValue) => string;
    translateFilter: (value: string) => string;
    translateMonth: (value: Month) => string;
    // Used to get translated question text
    getTranslation: GetTranslation;
}

const Context = createContext<ContextResponse | null>(null);

const languages = [sv, en];
const defaultLanguage = languages[0];

export function TranslationProvider({ children }: Props): JSX.Element {
    const [translation, setTranslation] = useState(defaultLanguage);

    const value = useMemo<ContextResponse>(
        () => ({
            translation,
            languages,

            changeLanguage: (language) => {
                const nextTranslation = languages.find(
                    (t) => t.code === language,
                );
                if (nextTranslation == null) {
                    console.error("Translation not found", language);
                    return;
                }
                setTranslation(nextTranslation);
            },

            translateDimension: (value) => {
                return translation.dimensions[value] ?? value;
            },

            translateFilter: (value) => {
                return (
                    (translation.filter != null &&
                    isKeyOf(translation.filter, value)
                        ? translation.filter[value]
                        : undefined) ?? value
                );
            },

            translateMonth: (value) => {
                return translation.months[value];
            },

            getTranslation: (texts) => {
                return texts[translation.code] ?? texts[defaultLanguage.code];
            },
        }),
        [translation],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useTranslation(): ContextResponse {
    const context = useContext(Context);
    if (context == null) {
        throw new Error("Translation context is missing");
    }
    return context;
}

function isKeyOf<T extends object>(
    obj: T,
    key: string,
): key is Extract<keyof T, string> {
    return key in obj;
}
